import { normalizeBadgeLevel } from "@components/utils/normalizeBadgeLevel";
import { ref, computed, nextTick, getCurrentInstance } from "vue";
import {
  createProficiencyBadge,
  getEmployeeBadges,
  deleteResponsibilityBadge,
  getEmployeeChecklist,
  getEmployeeCheckpointDetail,
  notApplicableCheckPointEvaluation,
  resetCheckPointEvaluation,
  recalculateEmployeeReadiness,
  getChatterList,
} from "@/api";
import {
  extractCreatedProficiencyBadgeId,
  proficiencyCheckpointErrorMessage,
} from "@/utils/proficiencyBadgeHelpers";
import { transformChatterData } from "@/utils/chatterUtils";
import {
  buildCoachChecklistInspectionLevels,
  isCoachChecklistLevelCompleteForAward,
} from "@components/utils/buildCoachChecklistInspectionLevels";
import { resolveStoredAuthEmployeeId } from "@components/utils/authEmployeeContext";
import {
  resolveChecklistQueryParams,
  resolveLevelName,
} from "@components/utils/buildChecklistInspectionLevels";

/**
 * As a Coach inspection state + handlers shared by AsACoachTab (full page) and
 * ResponsibilityBadgeHoldersTab (drawers). Must be called from `setup`.
 *
 * @param {object} [options]
 * @param {() => Promise<void>|void} [options.onListRefresh] refetch the host's list after a badge change
 * @param {() => void} [options.onViewClosed] host cleanup after the checklist view resets (e.g. route query)
 */
export function useCoachInspect({ onListRefresh = async () => {}, onViewClosed = null } = {}) {
  const { proxy } = getCurrentInstance();

  const showInspectChecklistView = ref(false);
  const coachChecklistItems = ref([]);
  const coachChecklistPassFilter = ref("not-yet-passed");
  const coachChecklistSearchQuery = ref("");
  const coachChecklistExpandedLevel = ref(undefined);
  const loggedInUserBadgeLevel = ref("");
  const loggedInUserArchitectWillCoach = ref(false);
  const showCoachCheckpointPanel = ref(false);
  const coachCheckpointPanelWidth = ref(920);
  const coachPanelCheckpointItem = ref(null);
  const coachPanelCheckpointDetail = ref(null);
  const coachPanelCheckpointGroup = ref(null);
  const coachPanelActionId = ref("");
  const showCoachMarkNaModal = ref(false);
  const coachMarkNaReason = ref("");
  const coachMarkNaContext = ref(null);
  const showCoachAwardBadgeDialog = ref(false);
  const coachAwardBadgeLevel = ref("");
  const pendingCoachAwardLevel = ref(null);

  // Chatter drawer state
  const showChatterDrawer = ref(false);
  const selectedBadgeChatter = ref(null);
  const chatterData = ref([]);
  const chatterPage = ref(1);
  const totalChatter = ref(0);
  const chatterLoading = ref(false);
  const chatterType = ref(null);
  const chatterTypeValue = ref(null);
  const chatterIconName = ref(null);

  const showEmployeeModal = ref(false);
  const showUpgradeBadgeDialog = ref(false);
  const selectedEmployee = ref(null);
  const selectedResponsibility = ref(null);
  const selectedRowData = ref(null);
  const currentAction = ref("");
  const giveBadgeLockedLevel = ref(null);
  const giveBadgeFromInspection = ref(false);
  const upgradeBadgeFromInspection = ref(false);

  const currentBadgeLevel = computed(() => {
    const badges = selectedEmployee.value?.badges || [];
    const respCode = selectedResponsibility.value?.code;

    let currentBadge;
    if (respCode) {
      const normalizedCode = respCode.startsWith("#") ? respCode.substring(1) : respCode;
      currentBadge = badges.find((b) => {
        const issuedCode = b.issued_for?.code?.match(/#(\d+)/)?.[1];
        return issuedCode === normalizedCode && b.is_current == 1;
      });
    } else {
      currentBadge = badges.find((b) => b.is_current);
    }

    const level = currentBadge?.level;
    if (!level) return null;

    const levelMap = { 1: "apprentice", 2: "professional", 3: "coach", 4: "master" };
    const levelStr = typeof level === "string" ? level?.toLowerCase() : levelMap[level];
    const validLevels = ["apprentice", "professional", "coach", "master"];
    return validLevels.includes(levelStr) ? levelStr : null;
  });

  /** UpgradeBadgeDialog requires a non-null level; fallback to apprentice when opening upgrade. */
  const currentBadgeLevelForUpgrade = computed(() => {
    return currentBadgeLevel.value || "apprentice";
  });

  /** Notify checkbox only when access exists and is allowed (`access == null` must not show Notify). */
  const coachNotifyEligible = computed(() => {
    const a = selectedEmployee.value?.access;
    if (a == null) return false;
    if (a.is_suspended === true) return false;
    if (a.has_access === false) return false;
    return true;
  });

  /** `access == null` / missing → show "needs to be verified" instead of generic no-access copy. */
  const coachAccessNeedsVerification = computed(() => selectedEmployee.value?.access == null);

  const currentEmployeeForAwardDialog = computed(() => selectedEmployee.value ?? {});

  const inspectChecklistResponsibility = computed(() => {
    const ra = selectedRowData.value?.responsibilityAssignments || {};
    return {
      responsibilityCode: ra.responsibility_code || "",
      responsibilityName: ra.responsibility_name || "",
      responsibilityImportance: ra.responsibility_importance ?? 3,
      roleCode: ra.role_code || "",
      roleName: ra.role_name || "",
      roleImportance: ra.role_importance ?? 3,
      positionCode: ra.position_code || "",
      positionName: ra.position_name || "",
      positionImportance: ra.position_importance ?? 3,
      positionCommitment: ra.position_commitment || "Primary",
      assignedOn: ra.assigned_on || null,
      newDuration: ra.new_duration || 15,
      show_codes: true,
    };
  });

  const inspectChecklistJobTitle = computed(() => {
    return (
      selectedRowData.value?.user?.position ||
      selectedEmployee.value?.title ||
      selectedRowData.value?.originalEmployee?.title ||
      selectedRowData.value?.originalEmployee?.primary_position?.name ||
      ""
    );
  });

  const coachPrimaryPositionName = computed(() => {
    const original = selectedRowData.value?.originalEmployee;
    return (
      original?.primary_position?.name ||
      original?.primary_positions?.[0]?.name ||
      selectedRowData.value?.user?.position ||
      ""
    );
  });

  const coachPreparingForPositionName = computed(() => {
    const original = selectedRowData.value?.originalEmployee;
    return (
      original?.preparing_for_position?.name || original?.preparing_for_positions?.[0]?.name || ""
    );
  });

  const coachChecklistLevels = computed(() =>
    buildCoachChecklistInspectionLevels(coachChecklistItems.value, {
      passFilter: coachChecklistPassFilter.value,
      search: coachChecklistSearchQuery.value,
      expandedLevel: coachChecklistExpandedLevel.value,
      currentBadgeLevel: selectedRowData.value?.badge?.level || "Novice",
      currentBadge: selectedRowData.value?.badge || null,
      loggedInUserBadgeLevel: loggedInUserBadgeLevel.value,
      loggedInUserArchitectWillCoach: loggedInUserArchitectWillCoach.value,
    })
  );

  const coachPanelCheckpointMerged = computed(() => {
    if (!coachPanelCheckpointItem.value) return null;
    return coachPanelCheckpointDetail.value
      ? { ...coachPanelCheckpointItem.value, ...coachPanelCheckpointDetail.value }
      : coachPanelCheckpointItem.value;
  });

  const coachPanelLevel = computed(() => {
    if (coachPanelCheckpointGroup.value?.level) return coachPanelCheckpointGroup.value.level;
    if (!coachPanelCheckpointItem.value) return "Apprentice";
    return resolveLevelName(coachPanelCheckpointItem.value);
  });

  const coachPanelCheckpointId = computed(() => {
    const item = coachPanelCheckpointMerged.value || coachPanelCheckpointItem.value;
    return item?.id ?? item?.checkpoint_id ?? null;
  });

  const extractCodeNumber = (codeStr) => {
    const match = codeStr?.match(/#(\d+)/);
    return match ? match[1] : null;
  };

  const COACH_CHECKPOINT_PANEL_ACTION_IDS = new Set(["inspect-now", "re-inspect"]);

  function resolveCoachCheckpointPayload(sourceItem, level) {
    const row = sourceItem?.item ?? sourceItem ?? {};
    return {
      employee_id: selectedEmployee.value?.id,
      responsibility_id: selectedResponsibility.value?.id,
      checkpoint_id: row?.id ?? row?.checkpoint_id,
      level: level || resolveLevelName(row),
      item: row,
    };
  }

  async function findLoggedInUserBadgeAcrossPages(
    accountId,
    employeeId,
    responsibilityId,
    extraQuery = {}
  ) {
    let offset = 0;
    let continueFetching = true;

    while (continueFetching) {
      const { data } = await getEmployeeBadges(accountId, employeeId, {
        status: "current",
        limit: 20,
        offset,
        ...extraQuery,
      });
      const results = Array.isArray(data?.results) ? data.results : [];
      const badge = findCurrentBadgeForResponsibility(results, responsibilityId);
      if (badge) return badge;

      if (!results.length || !data?.next) {
        continueFetching = false;
      } else {
        offset += results.length;
      }
    }

    return null;
  }

  function isArchitectWillCoachFlag(badge) {
    const value = badge?.architect_will_coach ?? badge?.master_will_coach;
    return value === true || value === 1 || value === "1";
  }

  async function loadLoggedInUserBadgeForResponsibility(responsibilityId) {
    loggedInUserBadgeLevel.value = "";
    loggedInUserArchitectWillCoach.value = false;

    const accountId = localStorage.getItem("currentAccountID");
    const loggedInEmployeeId = resolveStoredAuthEmployeeId();
    if (!accountId || !loggedInEmployeeId || responsibilityId == null || responsibilityId === "") {
      return;
    }

    try {
      const numericRespId = Number(responsibilityId);
      const canSendRespId = Number.isFinite(numericRespId) && numericRespId > 0;

      let badge = null;
      if (canSendRespId) {
        badge = await findLoggedInUserBadgeAcrossPages(
          accountId,
          loggedInEmployeeId,
          responsibilityId,
          { responsibility_id: numericRespId }
        );
      }
      if (!badge) {
        badge = await findLoggedInUserBadgeAcrossPages(
          accountId,
          loggedInEmployeeId,
          responsibilityId
        );
      }
      if (!badge) return;

      const slice = buildCoachBadgeSliceFromApiRow(badge);
      loggedInUserBadgeLevel.value = slice?.level || "";
      loggedInUserArchitectWillCoach.value = isArchitectWillCoachFlag(badge);
    } catch (error) {
      console.error("Failed to load logged-in user badge for inspect checklist:", error);
    }
  }

  async function fetchCoachChecklist({ preserveExpanded = false, skipLoader = false } = {}) {
    const accountId = localStorage.getItem("currentAccountID");
    const employeeId = selectedEmployee.value?.id;
    const responsibilityId = selectedResponsibility.value?.id;
    if (!accountId || !employeeId || !responsibilityId) return;

    const expandedLevel = coachChecklistExpandedLevel.value;

    try {
      if (!skipLoader) proxy.$loader.show();
      const { data } = await getEmployeeChecklist(
        accountId,
        responsibilityId,
        employeeId,
        resolveChecklistQueryParams("all")
      );
      coachChecklistItems.value = Array.isArray(data) ? data : data?.results ?? [];
      if (preserveExpanded) coachChecklistExpandedLevel.value = expandedLevel;
    } catch (error) {
      console.error("Failed to load coach checklist:", error);
      coachChecklistItems.value = [];
    } finally {
      if (!skipLoader) proxy.$loader.hide();
    }
  }

  async function reloadCoachChecklistAndBadge({
    preserveExpanded = true,
    skipLoader = false,
  } = {}) {
    try {
      if (!skipLoader) proxy.$loader.show();
      await fetchCoachChecklist({ preserveExpanded, skipLoader: true });
      const accountId = localStorage.getItem("currentAccountID");
      const employeeId = selectedEmployee.value?.id;
      const responsibilityId = selectedResponsibility.value?.id;
      if (!accountId || !employeeId || !responsibilityId) return;

      const badgesRes = await getEmployeeBadges(accountId, employeeId);
      const results = badgesRes?.data?.results || [];
      const currentBadge = findCurrentBadgeForResponsibility(
        results,
        responsibilityId,
        selectedRowData.value
      );
      if (selectedRowData.value && currentBadge) {
        const slice = buildCoachBadgeSliceFromApiRow(currentBadge);
        selectedRowData.value = {
          ...selectedRowData.value,
          badge: {
            ...selectedRowData.value.badge,
            ...slice,
          },
        };
      }
    } catch (e) {
      console.error("reloadCoachChecklistAndBadge badge sync:", e);
    } finally {
      if (!skipLoader) proxy.$loader.hide();
    }
  }

  function onCoachChecklistPassFilterUpdate(value) {
    coachChecklistPassFilter.value = value;
  }

  function onCoachChecklistPassFilterChange() {
    coachChecklistExpandedLevel.value = undefined;
  }

  function handleCoachChecklistToggleLevel({ level, expanded }) {
    coachChecklistExpandedLevel.value = expanded ? level : null;
  }

  function handleCoachChecklistBadgeClick({ level, badgeAction }) {
    if (badgeAction === "Award") {
      const targetNum = getBadgeLevelByLabel(level);
      const currentNum = getCurrentBadgeLevelNumberCoach();
      if (targetNum <= currentNum) {
        proxy.$notifications?.error(
          "You cannot award a lower badge. Remove the existing badge first."
        );
        return;
      }
      if (!isLevelCompleteForAwardCoach(level)) {
        proxy.$notifications?.error(
          "Complete all checkpoints at lower levels before awarding this badge."
        );
        return;
      }
      openCoachAwardBadgeDialog(level);
      return;
    }
    if (badgeAction === "Give Badge") {
      const targetNum = getBadgeLevelByLabel(level);
      const currentNum = getCurrentBadgeLevelNumberCoach();
      if (targetNum <= currentNum) {
        proxy.$notifications?.error(
          "You cannot give a lower badge. Remove the existing badge first."
        );
        return;
      }
      giveBadgeFromInspection.value = true;
      giveBadgeLockedLevel.value = normalizeAwardLevelForDialog(level);
      showEmployeeModal.value = true;
    }
  }

  function handleCoachChecklistBadgeRemove(payload) {
    processRemoveBadgeFromEvalModal({
      level: payload?.level,
      id: payload?.badge?.id,
      currentBadge: payload?.badge,
    });
  }

  async function handleCoachChecklistBadgeExpirationSynced() {
    const empId = selectedEmployee.value?.id;
    const respId = selectedResponsibility.value?.id;
    if (empId && respId) {
      await syncInspectionPaneBadgeFromApi(empId, respId);
      await reloadCoachChecklistAndBadge();
    }
    await onListRefresh();
  }

  function openCoachAwardBadgeDialog(level) {
    coachAwardBadgeLevel.value = normalizeAwardLevelForDialog(level);
    showCoachAwardBadgeDialog.value = true;
  }

  function closeCoachAwardBadgeDialog() {
    showCoachAwardBadgeDialog.value = false;
    coachAwardBadgeLevel.value = "";
  }

  function getAwardBadgeErrorMessage(error) {
    const data = error?.response?.data;
    const message = Array.isArray(data) ? data[0] : data?.detail;
    return typeof message === "string" && message.trim() ? message : "Error awarding badge";
  }

  async function handleCoachAwardBadgeActivate(isNotify) {
    const accountID = localStorage.getItem("currentAccountID");
    const levelIntMap = { apprentice: 1, proficient: 2, coach: 3, architect: 4 };
    const level = coachAwardBadgeLevel.value || "apprentice";
    const levelId = levelIntMap[normalizeBadgeLevel(level)] ?? 1;

    if (!accountID || !selectedEmployee.value?.id || !selectedResponsibility.value?.id) return;

    try {
      proxy.$loader.show();
      const createRes = await createProficiencyBadge(accountID, {
        employee: selectedEmployee.value.id,
        responsibility: selectedResponsibility.value.id,
        level: levelId,
        given: false,
        issued_method: "awarded",
        notify: isNotify !== undefined ? isNotify : true,
      });
      const createdBadgeId = extractCreatedProficiencyBadgeId(createRes);
      proxy.$notifications?.success(
        `Badge ${level} was successfully awarded to ${selectedEmployee.value?.fullName}`
      );
      closeCoachAwardBadgeDialog();
      const empId = selectedEmployee.value.id;
      const respId = selectedResponsibility.value.id;
      applyCoachInspectionBadgeLocalUpdate({
        level: levelId,
        id: createdBadgeId,
        status: "Good Standing",
        issuedMethod: "awarded",
      });
      await reloadCoachChecklistAndBadge({ skipLoader: true });
      await syncInspectionPaneBadgeFromApi(empId, respId);
      await onListRefresh();
    } catch (error) {
      console.error("Error awarding badge:", error);
      proxy.$notifications?.error(getAwardBadgeErrorMessage(error));
    } finally {
      proxy.$loader.hide();
    }
  }

  async function openCoachCheckpointPanel({ group, checkpoint, id }) {
    const item = checkpoint?.item || checkpoint;
    if (!item) return;

    coachCheckpointPanelWidth.value = Math.round(window.innerWidth * 0.8);
    coachPanelCheckpointGroup.value = group || null;
    coachPanelCheckpointItem.value = item;
    coachPanelCheckpointDetail.value = null;
    coachPanelActionId.value = id || "";

    const accountId = localStorage.getItem("currentAccountID");
    const responsibilityId = selectedResponsibility.value?.id;
    const checkpointId = item?.id ?? item?.checkpoint_id;

    try {
      proxy.$loader.show();
      if (accountId && responsibilityId && checkpointId) {
        const { data } = await getEmployeeCheckpointDetail(
          accountId,
          responsibilityId,
          checkpointId
        );
        coachPanelCheckpointDetail.value = data ?? null;
      }
      showCoachCheckpointPanel.value = true;
    } catch (error) {
      console.error("Error loading checkpoint detail:", error);
      showCoachCheckpointPanel.value = true;
    } finally {
      proxy.$loader.hide();
    }
  }

  async function closeCoachCheckpointPanel() {
    showCoachCheckpointPanel.value = false;
    await nextTick();
    coachPanelCheckpointItem.value = null;
    coachPanelCheckpointDetail.value = null;
    coachPanelCheckpointGroup.value = null;
    coachPanelActionId.value = "";
  }

  function openCoachMarkNaModal(payload) {
    coachMarkNaContext.value = payload;
    coachMarkNaReason.value = "";
    showCoachMarkNaModal.value = true;
  }

  function closeCoachMarkNaModal() {
    showCoachMarkNaModal.value = false;
    coachMarkNaReason.value = "";
    coachMarkNaContext.value = null;
  }

  async function confirmCoachMarkNa() {
    const reason = coachMarkNaReason.value?.trim();
    if (!reason) return;

    const { checkpoint, group } = coachMarkNaContext.value || {};
    const item = checkpoint?.item || checkpoint;
    if (!item) return;

    coachPanelCheckpointItem.value = item;
    coachPanelCheckpointGroup.value = group || null;

    const payload = resolveCoachCheckpointPayload(item, coachPanelLevel.value);
    if (!payload.checkpoint_id) {
      closeCoachMarkNaModal();
      return;
    }

    try {
      proxy.$loader.show();
      await evaluateCoachCheckpoint(payload, "not_applicable", reason);
      closeCoachMarkNaModal();
      await reloadCoachChecklistAndBadge({ skipLoader: true });
      await nextTick();
      maybePromptCoachAwardAfterPass(payload);
      proxy.$notifications?.success("Checkpoint marked as not applicable");
    } catch (e) {
      const msg =
        proficiencyCheckpointErrorMessage(e) || "Error marking checkpoint as not applicable";
      proxy.$notifications?.error(msg);
      console.error("Error marking checkpoint as not applicable:", e);
    } finally {
      proxy.$loader.hide();
    }
  }

  function handleCoachChecklistActionClick(payload) {
    const { id, checkpoint, group } = payload || {};
    if (id === "reset") {
      coachPanelCheckpointItem.value = checkpoint?.item || checkpoint;
      coachPanelCheckpointGroup.value = group || null;
      handleCoachPanelReset();
      return;
    }
    if (id === "mark-not-applicable") {
      openCoachMarkNaModal(payload);
      return;
    }
    if (COACH_CHECKPOINT_PANEL_ACTION_IDS.has(id)) {
      openCoachCheckpointPanel(payload);
    }
  }

  function handleCoachChecklistSecondaryActionClick(payload) {
    if (payload?.id === "mark-not-applicable") {
      openCoachMarkNaModal(payload);
    }
  }

  async function evaluateCoachCheckpoint(payload, status, reason) {
    const accountId = localStorage.getItem("currentAccountID");
    const { responsibility_id, checkpoint_id, employee_id } = payload;
    const body = {
      employee: employee_id,
      checkpoint: checkpoint_id,
      reason,
      status,
    };

    if (status === "not_applicable") {
      await notApplicableCheckPointEvaluation(accountId, responsibility_id, checkpoint_id, body);
    } else if (status === "reset") {
      await resetCheckPointEvaluation(accountId, responsibility_id, checkpoint_id, body);
    }
  }

  function maybePromptCoachAwardAfterPass(payload) {
    let levelToShow = null;
    if (pendingCoachAwardLevel.value) {
      const n = getBadgeLevelByLabel(pendingCoachAwardLevel.value);
      if (n > 0 && n > getCurrentBadgeLevelNumberCoach()) {
        levelToShow = pendingCoachAwardLevel.value;
      }
    } else if (payload?.level && shouldShowAwardBadgeForLevelCoach(payload.level)) {
      levelToShow = payload.level;
    }
    pendingCoachAwardLevel.value = null;
    if (levelToShow) {
      openCoachAwardBadgeDialog(levelToShow);
    }
  }

  async function handleCoachPanelEvaluated(payload) {
    const closePanel = ["passed", "failed", "not_applicable"].includes(payload?.status);
    if (closePanel) {
      await closeCoachCheckpointPanel();
    }

    try {
      await reloadCoachChecklistAndBadge();
      if (!closePanel) return;

      await nextTick();
      if (payload?.status === "passed" || payload?.status === "not_applicable") {
        maybePromptCoachAwardAfterPass({
          employee_id: payload?.employeeId,
          responsibility_id: payload?.responsibilityId,
          checkpoint_id: payload?.checkpointId,
          level: payload?.level,
        });
      }
    } catch (error) {
      console.error("Error syncing coach panel after evaluation:", error);
    }
  }

  async function handleCoachPanelReset() {
    const payload = resolveCoachCheckpointPayload(
      coachPanelCheckpointItem.value,
      coachPanelLevel.value
    );
    if (!payload.checkpoint_id) return;

    const accountId = localStorage.getItem("currentAccountID");
    const responsibilityId = selectedResponsibility.value?.id;
    const checkpointId = coachPanelCheckpointId.value;

    try {
      proxy.$loader.show();
      await evaluateCoachCheckpoint(payload, "reset", "reset");
      await reloadCoachChecklistAndBadge({ skipLoader: true });
      if (accountId && responsibilityId && checkpointId) {
        const { data } = await getEmployeeCheckpointDetail(
          accountId,
          responsibilityId,
          checkpointId
        );
        coachPanelCheckpointDetail.value = data ?? null;
      }
      proxy.$notifications?.success("Checkpoint reset");
    } catch (e) {
      console.error("Error resetting checkpoint:", e);
      proxy.$notifications?.error("Error resetting checkpoint");
    } finally {
      proxy.$loader.hide();
    }
  }

  /** Path to Readiness parity: numeric tier 0–4 from label or API number. */
  function getBadgeLevelByLabel(level) {
    if (level == null || level === "") return 0;
    if (typeof level === "number") {
      if (level >= 1 && level <= 4) return level;
      return 0;
    }
    const s = normalizeBadgeLevel(String(level));
    switch (s) {
      case "novice":
        return 0;
      case "apprentice":
        return 1;
      case "proficient":
        return 2;
      case "coach":
        return 3;
      case "architect":
        return 4;
      default:
        return 0;
    }
  }

  function getCurrentBadgeLevelNumberCoach() {
    const b = selectedRowData.value?.badge;
    if (!b?.level && b?.level !== 0) return 0;
    const lvl = b.level;
    if (typeof lvl === "number") {
      if (lvl >= 1 && lvl <= 4) return lvl;
      return getBadgeLevelByLabel(lvl);
    }
    return getBadgeLevelByLabel(String(lvl));
  }

  function normalizeAwardLevelForDialog(level) {
    if (level == null) return "apprentice";
    const raw = normalizeBadgeLevel(String(level));
    if (["apprentice", "proficient", "coach", "architect"].includes(raw)) return raw;
    return "apprentice";
  }

  /** All checklist rows for this tier and lower tiers must be passed before award. */
  function isLevelCompleteForAwardCoach(level) {
    return isCoachChecklistLevelCompleteForAward(level, coachChecklistItems.value);
  }

  function shouldShowAwardBadgeForLevelCoach(level) {
    if (!isLevelCompleteForAwardCoach(level)) return false;
    const currentLevelNum = getCurrentBadgeLevelNumberCoach();
    const levelStr = typeof level === "string" ? level : (level && String(level)) || "novice";
    const awardLevelNum = getBadgeLevelByLabel(levelStr);
    return awardLevelNum > 0 && awardLevelNum > currentLevelNum;
  }

  const closeInspectChecklistView = () => {
    showInspectChecklistView.value = false;
    closeCoachCheckpointPanel();
    closeCoachAwardBadgeDialog();
    selectedEmployee.value = null;
    selectedResponsibility.value = null;
    selectedRowData.value = null;
    currentAction.value = "";
    coachChecklistItems.value = [];
    coachChecklistPassFilter.value = "not-yet-passed";
    coachChecklistSearchQuery.value = "";
    coachChecklistExpandedLevel.value = undefined;
    loggedInUserBadgeLevel.value = "";
    loggedInUserArchitectWillCoach.value = false;
    pendingCoachAwardLevel.value = null;
    giveBadgeFromInspection.value = false;
    giveBadgeLockedLevel.value = null;
    upgradeBadgeFromInspection.value = false;
    onViewClosed?.();
  };

  const showRemoveBadgeDialog = ref(false);
  const removeBadgeDialogFromInspection = ref(false);
  const selectedBadgeForRemoval = ref(null);

  const giveBadgeDescription = computed(() => {
    if (giveBadgeFromInspection.value) {
      return "";
    }
    if (selectedResponsibility.value?.name) {
      return `Responsibility ${selectedResponsibility.value.name} has no Readiness Checklist therefore you can only Give (Instead of Awarding) a Badge to employee ${selectedEmployee.value?.fullName}.`;
    }
    return "";
  });

  const getBadgeLevelName = (level) => {
    if (typeof level === "string") {
      return level.charAt(0).toUpperCase() + level.slice(1).toLowerCase();
    }
    const levelMap = { 1: "Apprentice", 2: "Professional", 3: "Coach", 4: "Master" };
    return levelMap[level] || "Apprentice";
  };

  function findCurrentBadgeForResponsibility(results, responsibilityId, rowContext = null) {
    if (
      !Array.isArray(results) ||
      !results.length ||
      responsibilityId == null ||
      responsibilityId === ""
    ) {
      return null;
    }
    const rid = String(responsibilityId);
    const respCodeRaw =
      (rowContext && rowContext.responsibilityAssignments?.responsibility_code) ||
      selectedResponsibility.value?.code ||
      selectedRowData.value?.responsibilityAssignments?.responsibility_code ||
      "";
    const targetCode =
      extractCodeNumber(respCodeRaw) || String(respCodeRaw).replace(/^#/, "").split(" - ")[0] || "";

    const issuedCodeNorm = (b) => {
      const c = b?.issued_for?.code ?? b?.responsibility?.code ?? "";
      const m = String(c).match(/#(\d+)/);
      return m ? m[1] : String(c).replace(/^#/, "");
    };

    const matchesResp = (b) => {
      const idCandidates = [
        b.issued_for_id,
        b.issued_for?.id,
        b.responsibility?.id,
        b.responsibility_id,
      ]
        .filter((x) => x != null && x !== "")
        .map((x) => String(x));
      if (idCandidates.some((id) => id === rid)) return true;
      const ic = issuedCodeNorm(b);
      if (targetCode && ic && ic === targetCode) return true;
      return false;
    };

    // An Obsolete badge that has not been removed is still held: the backend refuses to award
    // the same or a lower level over it (ER-11638).
    const isHeldObsolete = (b) =>
      String(b?.status ?? "").toLowerCase() === "obsolete" && !b?.removed_on;

    const isCurrent = (b) =>
      b.is_current === true || b.is_current === 1 || b.is_current === "1" || isHeldObsolete(b);

    const issuedTs = (b) => {
      const raw = b?.issued_on || b?.created_at || b?.updated_at;
      if (!raw) return 0;
      const t = new Date(raw).getTime();
      return Number.isFinite(t) ? t : 0;
    };

    const numId = (b) => {
      const n = Number(b?.id);
      return Number.isFinite(n) ? n : 0;
    };

    const newerBadgeRow = (a, b) => {
      const ta = issuedTs(a);
      const tb = issuedTs(b);
      if (tb !== ta) return tb > ta ? b : a;
      const na = numId(a);
      const nb = numId(b);
      if (nb !== na) return nb > na ? b : a;
      return b;
    };

    const matched = results.filter(matchesResp);
    if (!matched.length) return null;

    const currentFlagged = matched.filter(isCurrent);
    if (currentFlagged.length === 1) return currentFlagged[0];
    if (currentFlagged.length > 1) {
      return currentFlagged.reduce(newerBadgeRow);
    }

    const isExplicitlyNotCurrent = (b) => {
      const ic = b?.is_current;
      return ic === false || ic === 0 || ic === "0" || ic === "false";
    };

    const candidates = matched.filter((b) => !isExplicitlyNotCurrent(b));
    if (candidates.length === 0) return null;
    if (candidates.length === 1) return candidates[0];
    return candidates.reduce(newerBadgeRow);
  }

  function buildCoachBadgeSliceFromApiRow(currentBadge) {
    if (!currentBadge) return null;
    const lvl = currentBadge.level;
    let levelStr;
    if (typeof lvl === "number") {
      levelStr = getBadgeLevelName(lvl);
    } else if (typeof lvl === "string" && lvl.length) {
      levelStr = lvl.charAt(0).toUpperCase() + lvl.slice(1).toLowerCase();
    } else {
      levelStr = "Novice";
    }
    return {
      id: currentBadge.id ?? null,
      level: levelStr,
      status: currentBadge.status || "No Dot",
      checkpoints: currentBadge.checkpoints || [],
      issued_on: currentBadge.issued_on ?? null,
      issued_method: currentBadge.issued_method ?? null,
      expiration_date: currentBadge.expiration_date ?? null,
      expires_in_days_from_becoming_obsolete:
        currentBadge.expires_in_days_from_becoming_obsolete ?? null,
      has_expiration_if_obsolete: !!currentBadge.has_expiration_if_obsolete,
      retries_to_expire_in_days: currentBadge.retries_to_expire_in_days ?? undefined,
    };
  }

  async function resolveBadgePayloadForInspectionRemove() {
    const accountId = localStorage.getItem("currentAccountID");
    const employeeId = selectedEmployee.value?.id;
    const responsibilityId = selectedResponsibility.value?.id;
    if (!accountId || !employeeId || !responsibilityId) return null;
    try {
      const badgesRes = await getEmployeeBadges(accountId, employeeId);
      const results = badgesRes?.data?.results || [];
      const row = findCurrentBadgeForResponsibility(results, responsibilityId);
      if (!row?.id) return null;
      const lvl = row.level;
      let cap;
      if (typeof lvl === "number") {
        cap = getBadgeLevelName(lvl);
      } else if (typeof lvl === "string" && lvl.length) {
        cap = lvl.charAt(0).toUpperCase() + lvl.slice(1).toLowerCase();
      } else {
        cap = "Novice";
      }
      return {
        id: row.id,
        level: cap,
        currentBadge: { id: row.id, level: cap },
      };
    } catch (e) {
      console.warn("resolveBadgePayloadForInspectionRemove:", e);
      return null;
    }
  }

  function applyCoachInspectionBadgeLocalUpdate({
    level,
    id = null,
    status = "Good Standing",
    issuedMethod = null,
  } = {}) {
    if (!selectedRowData.value || level == null || level === "") return;

    const levelStr =
      typeof level === "number"
        ? getBadgeLevelName(level)
        : typeof level === "string"
        ? level.charAt(0).toUpperCase() + level.slice(1).toLowerCase()
        : "Apprentice";

    selectedRowData.value = {
      ...selectedRowData.value,
      badge: {
        ...selectedRowData.value.badge,
        id: id ?? selectedRowData.value.badge?.id ?? null,
        level: levelStr,
        status: status ?? selectedRowData.value.badge?.status ?? "Good Standing",
        issued_method: issuedMethod ?? selectedRowData.value.badge?.issued_method ?? null,
      },
    };
  }

  async function syncInspectionPaneBadgeFromApi(employeeId, responsibilityId) {
    if (!employeeId || !responsibilityId || !selectedRowData.value) return;
    try {
      const accountId = localStorage.getItem("currentAccountID");
      const badgesRes = await getEmployeeBadges(accountId, employeeId);
      const results = badgesRes?.data?.results || [];
      const currentBadge = findCurrentBadgeForResponsibility(
        results,
        responsibilityId,
        selectedRowData.value
      );
      if (!currentBadge) return;

      const slice = buildCoachBadgeSliceFromApiRow(currentBadge);
      selectedRowData.value = {
        ...selectedRowData.value,
        badge: {
          ...selectedRowData.value.badge,
          ...slice,
        },
      };
    } catch (e) {
      console.warn("syncInspectionPaneBadgeFromApi:", e);
    }
  }

  const processRemoveBadgeFromEvalModal = async (data) => {
    try {
      let payload = data && typeof data === "object" ? { ...data } : { level: data };
      let id = payload.id != null && payload.id !== "" ? payload.id : null;
      if (!id) {
        const resolved = await resolveBadgePayloadForInspectionRemove();
        if (resolved?.id != null && resolved.id !== "") {
          id = resolved.id;
          payload = {
            ...payload,
            id: resolved.id,
            level: payload.level ?? resolved.level,
            currentBadge: payload.currentBadge ?? resolved.currentBadge,
          };
        }
      }
      if (!id) {
        throw new Error("badge id not found");
      }

      const badgeLevel = payload.level || payload.currentBadge?.level || "Apprentice";
      const badgeLevelName = getBadgeLevelName(
        typeof badgeLevel === "number" ? badgeLevel : String(badgeLevel || "Apprentice")
      );

      const responsibilityName =
        selectedResponsibility.value?.name ||
        selectedRowData.value?.responsibilityAssignments?.responsibility_name ||
        "";

      selectedBadgeForRemoval.value = {
        id,
        badgeName: badgeLevelName,
        responsibilityName,
        badgeData: payload,
      };

      removeBadgeDialogFromInspection.value = !!showInspectChecklistView.value;
      showRemoveBadgeDialog.value = true;
    } catch (e) {
      console.error(e);
      proxy.$notifications?.error("Error preparing badge removal");
    }
  };

  const handleCloseRemoveBadgeDialog = () => {
    showRemoveBadgeDialog.value = false;
    selectedBadgeForRemoval.value = null;
    removeBadgeDialogFromInspection.value = false;
  };

  const handleConfirmRemoveBadge = async () => {
    if (!selectedBadgeForRemoval.value?.id) return;

    const fromInspection = removeBadgeDialogFromInspection.value;
    const accountID = localStorage.getItem("currentAccountID");
    const badgeId = selectedBadgeForRemoval.value.id;

    proxy.$loader.show();
    try {
      const payload = {
        badge: badgeId,
        method: "removed",
        reason: "removed",
      };
      await deleteResponsibilityBadge(accountID, badgeId, payload);

      proxy.$notifications?.success("Badge removed successfully");
      showRemoveBadgeDialog.value = false;
      selectedBadgeForRemoval.value = null;
      removeBadgeDialogFromInspection.value = false;

      if (fromInspection) {
        if (selectedRowData.value) {
          selectedRowData.value = {
            ...selectedRowData.value,
            badge: {
              ...selectedRowData.value.badge,
              id: null,
              level: "Novice",
              status: "No Dot",
            },
          };
        }
        const empId = selectedEmployee.value?.id;
        if (accountID && empId) {
          try {
            await recalculateEmployeeReadiness(accountID, empId);
          } catch (e) {
            console.warn("recalculateEmployeeReadiness after remove:", e);
          }
        }
        if (
          showInspectChecklistView.value &&
          selectedResponsibility.value?.id &&
          selectedEmployee.value?.id
        ) {
          await reloadCoachChecklistAndBadge({ skipLoader: true });
        }
      } else {
        closeInspectChecklistView();
      }
      await onListRefresh();
    } catch (error) {
      console.error("Error removing badge:", error);
      proxy.$notifications?.error("Error removing badge");
      if (!fromInspection) {
        closeInspectChecklistView();
      }
    } finally {
      proxy.$loader.hide();
    }
  };

  const closeEmployeeModal = () => {
    showEmployeeModal.value = false;
    giveBadgeFromInspection.value = false;
    giveBadgeLockedLevel.value = null;
    if (!showInspectChecklistView.value) {
      selectedEmployee.value = null;
      selectedResponsibility.value = null;
      selectedRowData.value = null;
      currentAction.value = "";
    }
  };

  const closeUpgradeBadgeModal = () => {
    showUpgradeBadgeDialog.value = false;
    upgradeBadgeFromInspection.value = false;
    if (!showInspectChecklistView.value) {
      selectedEmployee.value = null;
      selectedResponsibility.value = null;
      selectedRowData.value = null;
      currentAction.value = "";
    }
  };

  const handleGiveBadge = async (event) => {
    const { level, notifyEmployee } = event;
    const accountID = localStorage.getItem("currentAccountID");

    if (!accountID || !selectedEmployee.value || !selectedResponsibility.value) {
      console.error("Missing required data for giving badge");
      return;
    }

    const levelIntMap = { apprentice: 1, proficient: 2, coach: 3, architect: 4 };
    const levelId = levelIntMap[normalizeBadgeLevel(level)] ?? level;
    const payload = {
      employee: selectedEmployee.value.id,
      responsibility: selectedResponsibility.value.id,
      level: levelId,
      given: true,
      issued_method: "given",
      forced: true,
      notify: notifyEmployee !== undefined ? notifyEmployee : true,
    };

    const keepInspectionOpen =
      giveBadgeFromInspection.value ||
      (showInspectChecklistView.value && upgradeBadgeFromInspection.value);

    try {
      proxy.$loader.show();
      const createRes = await createProficiencyBadge(accountID, payload);
      const createdBadgeId = extractCreatedProficiencyBadgeId(createRes);

      proxy.$notifications?.success(
        `Badge ${level} was successfully issued to ${selectedEmployee.value?.fullName} for Responsibility ${selectedResponsibility.value?.name} `
      );

      if (keepInspectionOpen) {
        giveBadgeFromInspection.value = false;
        upgradeBadgeFromInspection.value = false;
        giveBadgeLockedLevel.value = null;
        showEmployeeModal.value = false;
        showUpgradeBadgeDialog.value = false;

        applyCoachInspectionBadgeLocalUpdate({
          level: levelId,
          id: createdBadgeId,
          status: "Given",
          issuedMethod: "given",
        });
        const empId = selectedEmployee.value?.id;
        const respId = selectedResponsibility.value?.id;
        if (empId && respId) {
          await reloadCoachChecklistAndBadge({ skipLoader: true });
          await syncInspectionPaneBadgeFromApi(empId, respId);
        }
        await onListRefresh();
        return;
      }

      closeEmployeeModal();
      closeUpgradeBadgeModal();
      showInspectChecklistView.value = false;
      await onListRefresh();
    } catch (error) {
      console.error("Error giving badge:", error);
      if (giveBadgeFromInspection.value) {
        showEmployeeModal.value = false;
        giveBadgeFromInspection.value = false;
        giveBadgeLockedLevel.value = null;
      }
      if (upgradeBadgeFromInspection.value) {
        showUpgradeBadgeDialog.value = false;
        upgradeBadgeFromInspection.value = false;
      }
    } finally {
      proxy.$loader.hide();
    }
  };

  // Chatter drawer handlers
  const handleBadgeChatter = async (event) => {
    const badge = event?.badge;

    if (!badge || !badge.id) {
      return;
    }

    // Set badge chatter context
    selectedBadgeChatter.value = badge;
    chatterType.value = "badge";
    chatterTypeValue.value = getBadgeLevelName(badge.level);
    chatterIconName.value = "org_icon_responsibility";

    // Reset chatter data
    chatterData.value = [];
    chatterPage.value = 1;
    totalChatter.value = 0;
    showChatterDrawer.value = true;

    // Fetch chatter data
    await fetchChatter();
  };

  const closeChatterDrawer = () => {
    showChatterDrawer.value = false;
    selectedBadgeChatter.value = null;
    chatterData.value = [];
    chatterPage.value = 1;
    totalChatter.value = 0;
    chatterType.value = null;
    chatterTypeValue.value = null;
    chatterIconName.value = null;
  };

  const fetchChatter = async () => {
    if (
      chatterLoading.value ||
      !selectedBadgeChatter.value ||
      !selectedBadgeChatter.value.id ||
      !chatterType.value
    ) {
      return;
    }

    chatterLoading.value = true;
    try {
      const accountID = localStorage.getItem("currentAccountID");
      const recordId = selectedBadgeChatter.value.id;

      const { data } = await getChatterList(
        accountID,
        recordId,
        chatterType.value,
        chatterPage.value
      );

      if (data && data.count) {
        totalChatter.value = data.count;
      }

      if (data && Array.isArray(data.results)) {
        const items = transformChatterData(data.results);
        if (chatterPage.value === 1) {
          chatterData.value = items;
        } else {
          chatterData.value = [...chatterData.value, ...items];
        }
      }
    } catch (error) {
      console.error("Error fetching badge chatter:", error);
    } finally {
      chatterLoading.value = false;
    }
  };

  const loadMoreChatter = async () => {
    if (chatterLoading.value) return;
    chatterPage.value += 1;
    await fetchChatter();
  };

  /** Maps a coach-queue-shaped row onto selectedEmployee / selectedResponsibility. */
  function selectInspectionTarget(data) {
    const employeeData = data.originalEmployee || data;
    const respCode = data.responsibilityAssignments?.responsibility_code;
    const currentBadge =
      employeeData.badges?.find((b) => b.is_current && b.issued_for?.code === respCode) ||
      employeeData.badges?.find((b) => b.is_current);

    let badgesArray = employeeData.badges || [];
    if (
      badgesArray.length === 0 &&
      data.badge?.id &&
      data.badge?.level &&
      data.badge.level !== "Novice"
    ) {
      badgesArray = [
        {
          id: data.badge.id,
          is_current: 1,
          issued_for: {
            code: respCode,
            id: data.responsibilityAssignments?.responsibility_id,
            name: data.responsibilityAssignments?.responsibility_name,
          },
          level: data.badge.level,
        },
      ];
    }

    let accessForEmployee;
    if (employeeData.access === null) {
      accessForEmployee = null;
    } else if (employeeData.access && typeof employeeData.access === "object") {
      accessForEmployee = employeeData.access;
    } else {
      accessForEmployee = {
        is_suspended: employeeData.is_suspended || false,
        has_access: employeeData.has_access,
      };
    }

    selectedEmployee.value = {
      id: employeeData.employee_id || employeeData.id || data.id,
      first_name: employeeData.first_name || data.user?.name?.split(" ")[0] || "",
      last_name: employeeData.last_name || data.user?.name?.split(" ").slice(1).join(" ") || "",
      fullName:
        `${employeeData.first_name || ""} ${employeeData.last_name || ""}`.trim() ||
        data.user?.name ||
        "",
      avatar: employeeData.avatar || data.user?.avatar,
      email: employeeData.email || data.user?.email,
      badges: badgesArray,
      user: employeeData.user ?? data.user ?? null,
      access: accessForEmployee,
    };

    const cleanRespCode =
      extractCodeNumber(respCode) || respCode?.replace(/^#/, "")?.split(" - ")[0] || "";

    selectedResponsibility.value = {
      id:
        data.responsibilityAssignments?.responsibility_id ||
        employeeData.responsibility_id ||
        currentBadge?.issued_for?.id ||
        cleanRespCode,
      name:
        data.responsibilityAssignments?.responsibility_name ||
        employeeData.responsibility_name ||
        currentBadge?.issued_for?.name ||
        "",
      code: cleanRespCode ? `#${cleanRespCode}` : currentBadge?.issued_for?.code || "",
    };
  }

  async function openInspection(data) {
    selectInspectionTarget(data);
    selectedRowData.value = data;
    coachChecklistPassFilter.value = "not-yet-passed";
    coachChecklistSearchQuery.value = "";
    coachChecklistExpandedLevel.value = undefined;
    proxy.$loader.show();
    try {
      await loadLoggedInUserBadgeForResponsibility(selectedResponsibility.value?.id);
      await fetchCoachChecklist({ skipLoader: true });
      showInspectChecklistView.value = true;
      await nextTick();
    } finally {
      proxy.$loader.hide();
    }
  }

  const settableFields = {
    showEmployeeModal,
    showUpgradeBadgeDialog,
    showRemoveBadgeDialog,
    showCoachCheckpointPanel,
    coachMarkNaReason,
    coachChecklistSearchQuery,
  };

  function setInspectField(field, value) {
    if (settableFields[field]) settableFields[field].value = value;
  }

  return {
    showInspectChecklistView,
    coachChecklistItems,
    coachChecklistPassFilter,
    coachChecklistSearchQuery,
    coachChecklistExpandedLevel,
    loggedInUserBadgeLevel,
    loggedInUserArchitectWillCoach,
    showCoachCheckpointPanel,
    coachCheckpointPanelWidth,
    coachPanelCheckpointItem,
    coachPanelCheckpointDetail,
    coachPanelCheckpointGroup,
    coachPanelActionId,
    showCoachMarkNaModal,
    coachMarkNaReason,
    coachMarkNaContext,
    showCoachAwardBadgeDialog,
    coachAwardBadgeLevel,
    pendingCoachAwardLevel,
    showChatterDrawer,
    selectedBadgeChatter,
    chatterData,
    chatterPage,
    totalChatter,
    chatterLoading,
    chatterType,
    chatterTypeValue,
    chatterIconName,
    showEmployeeModal,
    showUpgradeBadgeDialog,
    selectedEmployee,
    selectedResponsibility,
    selectedRowData,
    currentAction,
    giveBadgeLockedLevel,
    giveBadgeFromInspection,
    upgradeBadgeFromInspection,
    currentBadgeLevel,
    currentBadgeLevelForUpgrade,
    coachNotifyEligible,
    coachAccessNeedsVerification,
    currentEmployeeForAwardDialog,
    inspectChecklistResponsibility,
    inspectChecklistJobTitle,
    coachPrimaryPositionName,
    coachPreparingForPositionName,
    coachChecklistLevels,
    coachPanelCheckpointMerged,
    coachPanelLevel,
    showRemoveBadgeDialog,
    removeBadgeDialogFromInspection,
    selectedBadgeForRemoval,
    giveBadgeDescription,
    extractCodeNumber,
    selectInspectionTarget,
    openInspection,
    setInspectField,
    fetchCoachChecklist,
    reloadCoachChecklistAndBadge,
    loadLoggedInUserBadgeForResponsibility,
    onCoachChecklistPassFilterUpdate,
    onCoachChecklistPassFilterChange,
    handleCoachChecklistToggleLevel,
    handleCoachChecklistBadgeClick,
    handleCoachChecklistBadgeRemove,
    handleCoachChecklistBadgeExpirationSynced,
    handleCoachChecklistActionClick,
    handleCoachChecklistSecondaryActionClick,
    closeCoachCheckpointPanel,
    closeCoachMarkNaModal,
    confirmCoachMarkNa,
    handleCoachPanelEvaluated,
    handleCoachPanelReset,
    closeCoachAwardBadgeDialog,
    handleCoachAwardBadgeActivate,
    closeInspectChecklistView,
    handleCloseRemoveBadgeDialog,
    handleConfirmRemoveBadge,
    closeEmployeeModal,
    closeUpgradeBadgeModal,
    handleGiveBadge,
    handleBadgeChatter,
    closeChatterDrawer,
    loadMoreChatter,
    findCurrentBadgeForResponsibility,
    buildCoachBadgeSliceFromApiRow,
    getBadgeLevelName,
  };
}
